import { useMemo } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAppData } from "store/appData";
import ExpandableChips from "./ExpandableChips";
import t from "./translations";
import {
  DATASET_API,
  type Dataset,
  type DatasetKind,
  type DefectDataset,
} from "./api";
import {
  formatProjectLabel,
  type ProjectNameDisplay,
} from "./hooks/useDatasetViewerSettings";

type DatasetRow = Dataset | DefectDataset;

function isObjectDataset(row: DatasetRow): row is Dataset {
  return "sensitive" in row;
}

interface DatasetGridProps {
  rows: DatasetRow[];
  loading: boolean;
  kind: DatasetKind;
  objectTypeMap: Map<number, string>;
  projectNameDisplay: ProjectNameDisplay;
}

function IdBadge({ id }: { id: number }) {
  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.palette.primary.main,
        borderRadius: "6px",
        padding: "2px",
        width: 25,
        height: 25,
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
      })}
    >
      <Typography variant="body2" sx={{ color: "white", fontWeight: 700 }}>
        {id}
      </Typography>
    </Box>
  );
}

export default function DatasetGrid({
  rows,
  loading,
  kind,
  objectTypeMap,
  projectNameDisplay,
}: DatasetGridProps) {
  const { projects } = useAppData();
  const navigate = useNavigate();
  const showSensitive = kind === "object";
  const detailBasePath = DATASET_API[kind].basePath;
  const columnCount = showSensitive ? 8 : 7;

  const projectMap = useMemo(
    () =>
      new Map(
        Object.values(projects).map((p) => [
          p.id,
          formatProjectLabel(p, projectNameDisplay),
        ])
      ),
    [projects, projectNameDisplay]
  );

  const projectChipMax = projectNameDisplay === "id" ? 12 : 2;

  return (
    <TableContainer
      component={Paper}
      sx={{ height: "100%" }}
      data-testid="dataset-table"
    >
      <Table
        stickyHeader
        size="small"
        sx={{
          tableLayout: "fixed",
          "& .MuiTableCell-root": { px: 1 },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell align="center" sx={{ width: 64 }}>
              ID
            </TableCell>
            <TableCell sx={{ width: 240 }}>{t.Name}</TableCell>
            <TableCell sx={{ width: 240 }}>{t.TypeList}</TableCell>
            <TableCell sx={{ width: 110 }}>{t.Date}</TableCell>
            {showSensitive && (
              <TableCell align="center" sx={{ width: 90 }}>
                {t.Sensitive}
              </TableCell>
            )}
            <TableCell>{t.Whitelist}</TableCell>
            <TableCell>{t.Blacklist}</TableCell>
            <TableCell>{t.SyntheticProjects}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columnCount} align="center">
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => (
            <TableRow
              key={row.id}
              hover
              onClick={() => navigate(`${detailBasePath}/${row.id}`)}
              data-id={row.id}
              data-testid={`datasetGrid.row.${kind}.${row.id}`}
              sx={{ cursor: "pointer" }}
            >
              <TableCell align="center">
                <IdBadge id={row.id} />
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                >
                  {row.name}
                </Typography>
              </TableCell>
              <TableCell>
                <ExpandableChips
                  ids={row.type_list}
                  labelMap={objectTypeMap}
                  max={3}
                  chipVariant="outlined"
                />
              </TableCell>
              <TableCell>
                {row.timestamp
                  ? new Date(row.timestamp).toLocaleDateString()
                  : "—"}
              </TableCell>
              {isObjectDataset(row) && (
                <TableCell align="center">
                  <Checkbox
                    checked={row.sensitive}
                    disabled
                    size="small"
                    sx={{ p: 0 }}
                  />
                </TableCell>
              )}
              <TableCell>
                <ExpandableChips
                  ids={row.approved_customers}
                  labelMap={projectMap}
                  max={projectChipMax}
                />
              </TableCell>
              <TableCell>
                <ExpandableChips
                  ids={row.non_included_customers}
                  labelMap={projectMap}
                  max={projectChipMax}
                />
              </TableCell>
              <TableCell>
                <ExpandableChips
                  ids={row.synthetic_customers}
                  labelMap={projectMap}
                  max={projectChipMax}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
